// Environment config for the tickets booking tool.
//
// Vendored from weebee_web/packages/api/src/config.ts when this app moved into
// wayq-transit, then reduced: there is no second backend, no App Check, and no
// Firebase emulator path, so `apiUrl`, `appCheck`, `useEmulators` and
// `authEmulatorUrl` are all gone. `transitApiUrl` is now REQUIRED — it used to
// fall back to the weebee `apiUrl`, and a silent fallback would now point the app
// at nothing.

function requireEnv(key: string): string {
  const value = import.meta.env[key] as string | undefined;
  if (!value) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}

function optionalEnv(key: string, fallback: string): string {
  return (import.meta.env[key] as string | undefined) ?? fallback;
}

export const config = Object.freeze({
  /** wayq-transit backend — the only backend this app talks to. */
  transitApiUrl: requireEnv('VITE_TRANSIT_API_URL'),
  environment: optionalEnv('VITE_ENVIRONMENT', 'dev'),
  // Sent as X-Client-Product so both backends attribute requests (metrics, logs,
  // origin_product). Must match the Go product vocabulary in
  // internal/shared/product/product.go.
  clientProduct: optionalEnv('VITE_CLIENT_PRODUCT', 'unknown'),
  // Seeds the booking contact store on first run. ONDC billing requires a phone
  // and Google sign-in never supplies one — see lib/contact.ts.
  defaultContactPhone: optionalEnv('VITE_DEFAULT_CONTACT_PHONE', ''),
  firebase: Object.freeze({
    // The WayQ *transit* Firebase project (wayq-transit-preprod / -prod), NOT
    // weebee. Transit's tenantauth maps this project's issuer to the `wayq`
    // tenant. See platform/firebase.ts for why this must not be "corrected".
    projectId: requireEnv('VITE_FIREBASE_PROJECT_ID'),
    apiKey: requireEnv('VITE_FIREBASE_API_KEY'),
    authDomain: requireEnv('VITE_FIREBASE_AUTH_DOMAIN'),
  }),
});

export type Config = typeof config;
